<template>
  <BaseModal :modalTitle="$t('W: Opdater svartider')">
    <div class="flex flex-col gap-5">
      <div
        v-if="orderedDeliveries.length === 0"
        class="text-sm text-text-muted">
        {{ $t('W: Ingen svartidsgrupper fundet') }}
      </div>

      <template v-else>
        <!-- One row per TAT group -->
        <div
          v-for="delivery in orderedDeliveries"
          :key="delivery.tatGroupID"
          class="flex flex-col gap-2">
          <!-- Group header: "Levering #X — TAT name" -->
          <div
            class="flex items-center justify-between"
            :class="{ 'opacity-60': isTatLocked(delivery) }">
            <span class="text-sm font-semibold text-text-primary">
              {{ $t('W: Levering') }} #{{ delivery.deliveryNumber }} —
              {{ delivery.tatName }}
            </span>
            <span class="text-xs text-text-muted">
              {{
                delivery.activeSampleCount === 1
                  ? $t('W: 1 prøve')
                  : $t('W: {count} prøver', {
                      count: delivery.activeSampleCount,
                    })
              }}
            </span>
          </div>

          <!-- Locked: read-only display -->
          <div
            v-if="isTatLocked(delivery)"
            class="flex items-center gap-1.5 rounded-md border border-border-subtle bg-surface-muted px-3 py-2">
            <IconLucideLock class="size-3.5 shrink-0 text-text-muted" />
            <span class="text-sm text-text-secondary">
              {{
                $t(
                  'W: Rapporten er planlagt, eller afsendelsen fejlede – svartiden kan ikke ændres'
                )
              }}
            </span>
          </div>

          <!-- TAT select (unlocked groups only) -->
          <UiInputSelectVee
            v-else
            :name="`tat-${delivery.tatGroupID}`"
            :label="$t('W: Svartid')"
            :options="availableTatOptionsForGroup(delivery.tatGroupID)"
            :modelValue="selectedTats[delivery.tatGroupID] ?? ''"
            @update:model-value="
              (val: string) => (selectedTats[delivery.tatGroupID] = val)
            "
            @change="onTatChange(delivery.tatGroupID)" />
        </div>

        <!-- Merge conflict banners — one per cluster, at the bottom -->
        <div
          v-for="cluster in conflictClusters"
          :key="cluster.tatID"
          class="border-feedback-warning-bg bg-feedback-warning-bg flex flex-col gap-2 rounded-md border p-3">
          <!-- Cluster header -->
          <span class="text-feedback-warning text-sm font-semibold">
            {{ clusterMemberLabel(cluster) }}
            {{ $t('W: vil have samme svartid') }}
          </span>

          <!-- All unlocked: can merge -->
          <template v-if="cluster.canMerge">
            <span class="text-feedback-warning text-sm">
              {{ $t('W: Slå dem sammen til én levering?') }}
            </span>
            <div class="flex gap-2">
              <UiBaseButton
                :text="$t('W: Slå sammen')"
                size="sm"
                :theme="
                  mergeDecisions[cluster.tatID] === true ? 'primary' : 'neutral'
                "
                type="button"
                @button-click="setMergeDecision(cluster, true)" />
              <UiBaseButton
                :text="$t('W: Behold adskilt')"
                size="sm"
                :theme="
                  mergeDecisions[cluster.tatID] === false
                    ? 'primary'
                    : 'neutral'
                "
                type="button"
                @button-click="setMergeDecision(cluster, false)" />
            </div>
          </template>

          <!-- Some locked: partial merge possible -->
          <template v-else-if="cluster.unlockedMemberIDs.length >= 2">
            <span class="text-feedback-warning text-sm">
              {{ lockedMemberLabel(cluster) }}
              {{ $t('W: er låst (godkendt/afsendt)') }}.
              {{ $t('W: De øvrige kan slås sammen – gøre det?') }}
            </span>
            <div class="flex gap-2">
              <UiBaseButton
                :text="$t('W: Slå sammen')"
                size="sm"
                :theme="
                  mergeDecisions[cluster.tatID] === true ? 'primary' : 'neutral'
                "
                type="button"
                @button-click="setMergeDecision(cluster, true)" />
              <UiBaseButton
                :text="$t('W: Behold adskilt')"
                size="sm"
                :theme="
                  mergeDecisions[cluster.tatID] === false
                    ? 'primary'
                    : 'neutral'
                "
                type="button"
                @button-click="setMergeDecision(cluster, false)" />
            </div>
          </template>

          <!-- Only 1 unlocked: cannot merge -->
          <template v-else>
            <span class="text-feedback-warning text-sm">
              {{
                $t(
                  'W: Kan ikke slå sammen – de øvrige leveringer er låst (godkendt/afsendt)'
                )
              }}
            </span>
          </template>
        </div>
      </template>

      <p
        v-if="errorText"
        class="text-feedback-error text-sm">
        {{ errorText }}
      </p>
    </div>

    <template #buttons>
      <UiBaseButton
        :text="$t('W: Gem')"
        :isLoading="isLoading"
        :showSaveIcon="showSaveIcon"
        :isDisabled="!hasChanges || hasUnresolvedMerge || isLoading"
        type="button"
        @button-click="onSubmit" />
    </template>
  </BaseModal>
</template>

<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { useOrderStore } from '@/stores/orderStore'
import { useAppStore } from '@/stores/appStore'
import { usePricingSegmentStore } from '@/stores/pricingSegmentStore'
import { useTurnaroundTimeStore } from '@/stores/turnaroundTimeStore'
import { useCustomerAccountStore } from '@/stores/customerAccountStore'
import { useInsuranceCustomerAccountStore } from '@/stores/insuranceCustomerAccountStore'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { isDeliveryTatChangeBlocked } from '@/globals/typescript/utils/deliveryTatLock'
import {
  getOrderedDeliveries,
  type IOrderedDelivery,
} from '@team-milva/lab-utils/orderDeliveries'
import type { PropType } from 'vue'
type ISelectOption = { value: string; text: string }

interface IConflictCluster {
  /** The TAT ID all members in this cluster would share */
  tatID: string
  tatName: string
  /** All group IDs in the cluster (includes locked) */
  memberIDs: string[]
  /** Subset that are locked (approved / sent / sent-failed) */
  lockedMemberIDs: string[]
  /** Subset that are NOT locked */
  unlockedMemberIDs: string[]
  /** True when at least 2 unlocked members can be merged */
  canMerge: boolean
}

export default {
  name: 'UpdateOrderTatsModal',

  emits: ['close'],

  props: {
    orderID: { type: String as PropType<string>, required: true },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),

      // tatGroupID → selected tatID (pre-filled with current tatIDs in created())
      selectedTats: {} as Record<string, string>,
      // clusterTatID → true (merge) | false (keep separate) | undefined (not decided)
      mergeDecisions: {} as Record<string, boolean>,
      isLoading: false,
      showSaveIcon: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useOrderStore, ['allOrders']),
    ...mapState(useAppStore, ['currentLocale', 'defaultMessageSaveIconTime']),
    ...mapState(usePricingSegmentStore, ['allPricingSegments']),
    ...mapState(useTurnaroundTimeStore, ['allTATs']),
    ...mapState(useCustomerAccountStore, ['allCustomerAccountPrices']),
    ...mapState(useInsuranceCustomerAccountStore, ['allInsuranceCustomerAccountPrices']),
    ...mapState(useAnalysisStore, ['allAnalyses']),

    order() {
      return this.allOrders.find((x) => x.id === this.orderID)
    },

    pricingSegment() {
      return this.allPricingSegments.find(
        (x) => x.id === this.customerPriceDoc?.pricingSegmentID
      )
    },

    // The owner's price document — a business customer's, or an insurance customer's own for an
    // order it placed itself (MIL-402), which lab-cloud prices the order by too.
    customerPriceDoc() {
      const order = this.order
      if (!order) return undefined
      if (order.customer.type === 'insurance') {
        return this.allInsuranceCustomerAccountPrices.find(
          (x) => x.insuranceCustomerAccountID === order.customer.id
        )
      }
      return this.allCustomerAccountPrices.find(
        (x) => x.customerAccountID === order.customerAccountID
      )
    },

    // Whether the order's TATs are gated by its owner's pricing — business and insurance
    // customers; a private order is not.
    hasCustomerPricing(): boolean {
      const customer = this.order?.customer
      return (
        this.order?.segment === 'business' &&
        !!customer &&
        (customer.type === 'business' || customer.type === 'insurance') &&
        !!customer.id
      )
    },

    availableTATsForOrder() {
      if (!this.hasCustomerPricing) {
        return [...this.allTATs]
      }
      const segmentTats = this.pricingSegment?.tats ?? []
      const customerTats = this.customerPriceDoc?.tats ?? []
      const tatMap = new Map<string, true>()
      for (const entry of segmentTats) {
        tatMap.set(entry.tatID, true)
      }
      for (const entry of customerTats) {
        // Only a fully-disabled TAT drops off; a per-analysis disable leaves
        // the TAT selectable for the analyses it still covers.
        if (entry.defaultAction === 'disabled' && entry.entries.length === 0) {
          tatMap.delete(entry.tatID)
        } else {
          tatMap.set(entry.tatID, true)
        }
      }
      return this.allTATs.filter((tat) => tatMap.has(tat.id))
    },

    /** Stable delivery-ordered list from the shared utility */
    orderedDeliveries(): IOrderedDelivery[] {
      if (!this.order) return []
      return getOrderedDeliveries(
        this.order.tatGroups ?? {},
        this.allTATs,
        this.currentLocale,
        this.order.samples ?? []
      )
    },

    /**
     * Clusters: groups all deliveries by their *effective TAT after user selection*.
     * A cluster is only a conflict when it has >1 member.
     */
    conflictClusters(): IConflictCluster[] {
      // Map tatID → deliveries that would share it after current selections
      const clusterMap = new Map<string, IOrderedDelivery[]>()

      for (const delivery of this.orderedDeliveries) {
        const selected = this.selectedTats[delivery.tatGroupID]
        const effectiveTatID: string =
          selected !== undefined ? selected : delivery.tatID
        const existing = clusterMap.get(effectiveTatID)
        if (existing) {
          existing.push(delivery)
        } else {
          clusterMap.set(effectiveTatID, [delivery])
        }
      }

      const clusters: IConflictCluster[] = []
      for (const [tatID, members] of clusterMap.entries()) {
        if (members.length <= 1) continue
        const tat = this.allTATs.find((x) => x.id === tatID)
        const lockedMemberIDs = members
          .filter((x) => x.isLocked)
          .map((x) => x.tatGroupID)
        const unlockedMemberIDs = members
          .filter((x) => !x.isLocked)
          .map((x) => x.tatGroupID)
        clusters.push({
          tatID,
          tatName: tat ? tat.getName(this.currentLocale) : tatID,
          memberIDs: members.map((x) => x.tatGroupID),
          lockedMemberIDs,
          unlockedMemberIDs,
          canMerge:
            unlockedMemberIDs.length >= 2 && lockedMemberIDs.length === 0,
        })
      }
      return clusters
    },

    hasChanges(): boolean {
      return this.orderedDeliveries.some(
        (delivery) =>
          !this.isTatLocked(delivery) &&
          this.selectedTats[delivery.tatGroupID] !== undefined &&
          this.selectedTats[delivery.tatGroupID] !== delivery.tatID
      )
    },

    hasUnresolvedMerge(): boolean {
      for (const cluster of this.conflictClusters) {
        if (cluster.unlockedMemberIDs.length < 2) continue
        if (!(cluster.tatID in this.mergeDecisions)) return true
      }
      return false
    },
  },

  methods: {
    ...mapActions(useOrderStore, ['updateOrderTatGroup']),

    setMergeDecision(cluster: IConflictCluster, decision: boolean): void {
      this.mergeDecisions = { ...this.mergeDecisions, [cluster.tatID]: decision }
    },

    /**
     * Returns the TAT dropdown options for a specific delivery group.
     * Uses the same logic as CreateOrderModal: TAT must meet the max
     * minimumWorkingDays across all analyses in the group, and for
     * custom-applicability TATs at least one analysis must be applicable.
     * The current TAT for the group is always included (ghost) even if
     * it would otherwise be filtered out.
     */
    availableTatOptionsForGroup(tatGroupID: string): ISelectOption[] {
      if (!this.order) return []

      // Collect analysis IDs for all active samples in this group
      const groupSamples = this.order.samples.filter(
        (s) => s.tatGroupID === tatGroupID && s.status !== 'cancelled'
      )
      const analysisIDs = [
        ...new Set(
          groupSamples.flatMap((s) =>
            s.analyses
              .filter((a) => a.timestamps.cancelledAt === null)
              .map((a) => a.analysisID)
          )
        ),
      ]

      // Max minimum working days across all analyses in this group (via minimumTurnaroundTimeID)
      const maxMinDays = analysisIDs.reduce((max, id) => {
        const analysis = this.allAnalyses.find((x) => x.id === id)
        const minTatID = analysis?.minimumTurnaroundTimeID
        const minTat = minTatID ? this.allTATs.find((x) => x.id === minTatID) : undefined
        const minDays = minTat?.type === 'fixed-deadline' ? minTat.workingDays : 0
        return Math.max(max, minDays)
      }, 0)

      // Min maximum working days across all analyses in this group (via maximumTurnaroundTimeID)
      const minMaxDays = analysisIDs.reduce((min, id) => {
        const analysis = this.allAnalyses.find((x) => x.id === id)
        const maxTatID = analysis?.maximumTurnaroundTimeID
        const maxTat = maxTatID ? this.allTATs.find((x) => x.id === maxTatID) : undefined
        const maxDays = maxTat?.type === 'fixed-deadline' ? maxTat.workingDays : Infinity
        return Math.min(min, maxDays)
      }, Infinity)

      const meetsMinimum = (tat: (typeof this.allTATs)[number]): boolean => {
        if (tat.type === 'walk-in') return maxMinDays === 0
        return tat.workingDays >= maxMinDays
      }

      const meetsMaximum = (tat: (typeof this.allTATs)[number]): boolean => {
        if (tat.type === 'walk-in') return true
        return tat.workingDays <= minMaxDays
      }

      let base: typeof this.allTATs
      if (!this.hasCustomerPricing) {
        base = this.allTATs.filter((x) => meetsMinimum(x) && meetsMaximum(x))
      } else {
        const segmentTats = this.pricingSegment?.tats ?? []
        const customerTats = this.customerPriceDoc?.tats ?? []
        const tatMap = new Map<
          string,
          { applicability: string; applicableAnalysisIDs: string[] }
        >()
        for (const entry of segmentTats) {
          const isCustom = entry.defaultPrice === null && entry.entries.length > 0
          tatMap.set(entry.tatID, {
            applicability: isCustom ? 'custom' : 'all',
            applicableAnalysisIDs: entry.entries.flatMap((e) => e.applicableAnalysisIDs),
          })
        }
        for (const entry of customerTats) {
          if (entry.defaultAction === 'disabled' && entry.entries.length === 0) {
            tatMap.delete(entry.tatID)
          } else if (!tatMap.has(entry.tatID)) {
            tatMap.set(entry.tatID, { applicability: 'all', applicableAnalysisIDs: [] })
          }
        }
        base = this.allTATs.filter((tat) => {
          const config = tatMap.get(tat.id)
          if (!config) return false
          if (config.applicability === 'custom') {
            const hasApplicable = analysisIDs.some((id) =>
              config.applicableAnalysisIDs.includes(id)
            )
            if (!hasApplicable) return false
          }
          return meetsMinimum(tat) && meetsMaximum(tat)
        })
      }

      // Always include the group's current TAT even if it no longer meets criteria
      const currentTatID = this.order.tatGroups[tatGroupID]?.tatID
      const ghost =
        currentTatID && !base.some((x) => x.id === currentTatID)
          ? this.allTATs.filter((x) => x.id === currentTatID)
          : []

      return [...base, ...ghost]
        .sort((a, b) =>
          a
            .getName(this.currentLocale)
            .localeCompare(b.getName(this.currentLocale))
        )
        .map((tat) => ({
          value: tat.id,
          text: tat.getName(this.currentLocale),
        }))
    },

    onTatChange(tatGroupID: string): void {
      const updatedDecisions = { ...this.mergeDecisions }
      for (const cluster of this.conflictClusters) {
        if (cluster.memberIDs.includes(tatGroupID)) {
          delete updatedDecisions[cluster.tatID]
        }
      }
      this.mergeDecisions = updatedDecisions
    },

    /** Returns a formatted list of delivery labels for a cluster, e.g. "Levering #1 og #2" */
    clusterMemberLabel(cluster: IConflictCluster): string {
      return cluster.memberIDs
        .map((id) => {
          const d = this.orderedDeliveries.find((x) => x.tatGroupID === id)
          return d
            ? this.$t('W: Levering #{number}', { number: d.deliveryNumber })
            : id
        })
        .join(` ${this.$t('W: og')} `)
    },

    /**
     * Whether a delivery's TAT is read-only. Narrower than "can be merged": only a scheduled report
     * or a failed send holds its TAT — an approved or sent delivery can still be re-timed (the frozen
     * PDFs carry none, and the TAT only feeds the invoice, which has its own lock). Such a delivery
     * still cannot be merged: `isLocked` keeps deciding that in the merge clusters below.
     */
    isTatLocked(delivery: IOrderedDelivery): boolean {
      return isDeliveryTatChangeBlocked(delivery.labReportStatus)
    },

    /** Returns a formatted list of locked delivery labels, e.g. "Levering #1" */
    lockedMemberLabel(cluster: IConflictCluster): string {
      return cluster.lockedMemberIDs
        .map((id) => {
          const d = this.orderedDeliveries.find((x) => x.tatGroupID === id)
          return d
            ? this.$t('W: Levering #{number}', { number: d.deliveryNumber })
            : id
        })
        .join(` ${this.$t('W: og')} `)
    },

    async onSubmit(): Promise<void> {
      if (!this.hasChanges || this.hasUnresolvedMerge) return

      this.isLoading = true
      this.errorText = ''

      try {
        for (const cluster of this.conflictClusters) {
          const doMerge = this.mergeDecisions[cluster.tatID] === true
          if (doMerge) {
            // Survivor = lowest delivery number (first in orderedDeliveries sort)
            const survivorID = cluster.unlockedMemberIDs[0]
            const mergeIDs = cluster.unlockedMemberIDs.filter(
              (id) => id !== survivorID
            )
            if (!survivorID) continue
            const survivorTatID =
              this.selectedTats[survivorID] ??
              this.orderedDeliveries.find((x) => x.tatGroupID === survivorID)
                ?.tatID
            if (survivorTatID) {
              await this.updateOrderTatGroup(
                this.orderID,
                survivorID,
                survivorTatID,
                undefined
              )
            }
            for (const id of mergeIDs) {
              const tatID =
                this.selectedTats[id] ??
                this.orderedDeliveries.find((x) => x.tatGroupID === id)?.tatID
              if (tatID) {
                await this.updateOrderTatGroup(this.orderID, id, tatID, survivorID)
              }
            }
          }
        }

        // Handle non-merging changes (groups not in a merge cluster)
        const mergedGroupIDs = new Set(
          this.conflictClusters
            .filter((c) => this.mergeDecisions[c.tatID] === true)
            .flatMap((c) => c.unlockedMemberIDs)
        )

        for (const delivery of this.orderedDeliveries) {
          if (this.isTatLocked(delivery)) continue
          if (mergedGroupIDs.has(delivery.tatGroupID)) continue
          const tatID = this.selectedTats[delivery.tatGroupID]
          if (!tatID || tatID === delivery.tatID) continue
          await this.updateOrderTatGroup(
            this.orderID,
            delivery.tatGroupID,
            tatID,
            undefined
          )
        }

        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        useToast().success(this.$t('W: Svartider opdateret'))
        this.$emit('close')
      } catch {
        this.errorText = this.$t('W: Der opstod en fejl. Prøv igen.')
        useToast().error(this.$t('W: Kunne ikke opdatere svartider'))
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    const initialTats: Record<string, string> = {}
    for (const group of Object.values(this.order?.tatGroups ?? {})) {
      initialTats[group.tatGroupID] = group.tatID
    }
    this.selectedTats = initialTats

    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    //
    // Registered after the pre-fill above, so the baseline is the order's
    // current state and only a real change reads as dirty.
    this.unsavedGuard.register({
      snapshot: () => ({
        selectedTats: { ...this.selectedTats },
        mergeDecisions: { ...this.mergeDecisions },
      }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
